import * as Yup from 'yup';
import cardValidator from 'card-validator';
import { CalendarDateService, l10nEnService } from '@uplift/services';
import {
  ENGLISH_MATCHES_WITH_SPACE,
  ENGLISH_MATCHES,
  NUMBERS_MATCHES,
  PHONE_MATCHES,
  NUMBERS_LETTERS_SPACE_HYPHEN_MATCHES,
  NAME_MATCHES,
  LICENSE_MATCHES,
} from '@uplift/constants';


export const VALIDATION_CARD_NUMBER = Yup.string()
  .transform(v => v?.replace(/\D/g, '') ?? '')
  .test(
    'cardNumber',
    ({ value }) => cardValidator.number(value)?.card?.type === 'american-express'
      ? l10nEnService.validations.client.cardNumber.invalid(15)
      : l10nEnService.validations.client.cardNumber.invalid(16),
    value => {
      const validation = cardValidator.number(value);

      if (validation?.card?.type === 'american-express' && validation.isPotentiallyValid) {
        return true;
      }

      return validation.isValid;
    }
  )
  .test(
    'cardNumberLength',
    ({ value }) => cardValidator.number(value)?.card?.type === 'american-express'
      ? l10nEnService.validations.client.cardNumber.invalid(15)
      : l10nEnService.validations.client.cardNumber.invalid(16),
    value => cardValidator.number(value)?.card?.type === 'american-express'
      ? value?.length === 15
      : value?.length === 16
  )
  .required(l10nEnService.validations.client.cardNumber.empty);

export const VALIDATION_CARD_EXP_DATE = Yup.string()
  .test(
    'isValid',
    l10nEnService.validations.client.expiration.emptyInvalid,
    value => CalendarDateService.date(value, 'MM/YY', true).isValid(),
  )
  .test(
    'isFuture',
    l10nEnService.validations.client.expiration.inPast,
    value => {
      const parsedValue = CalendarDateService.date(value, 'MM/YY', true);
      const now = CalendarDateService.date();

      return parsedValue.isSameOrAfter(now, 'month');
    }
  )
  .required(l10nEnService.validations.client.expiration.emptyInvalid);

export const VALIDATION_CARD_CVC = Yup.string()
  .when('cardNumber', {
    is: (cardNumber: string) => cardValidator.number(cardNumber)?.card?.type === 'american-express',
    then: s => s.test(
      'cvcValidation',
      l10nEnService.validations.client.cvc.invalid(4),
      value => cardValidator.cvv(value, 4).isValid
    ),
    otherwise: s => s.test(
      'cvcValidation',
      l10nEnService.validations.client.cvc.invalid(3),
      value => cardValidator.cvv(value, 3).isValid
    )
  })
  .required(l10nEnService.validations.client.cvc.empty);

export const VALIDATION_ADDRESS = Yup.string()
  .matches(ENGLISH_MATCHES_WITH_SPACE, { message: l10nEnService.validations.couldContainEnglishWithSpace, excludeEmptyString: true })
  .max(50, l10nEnService.validations.lengthMatches(1, 50));

export const VALIDATION_ADDRESS_REQUIRED = VALIDATION_ADDRESS.required(l10nEnService.validations.requiredStreetAddress);

export const VALIDATION_CITY = Yup.string()
  .matches(ENGLISH_MATCHES_WITH_SPACE, l10nEnService.validations.couldContainEnglishWithSpace)
  .max(50, l10nEnService.validations.lengthMatches(1, 50))
  .required(l10nEnService.validations.requiredCity);

export const VALIDATION_STATE = Yup.string().nullable().required(l10nEnService.validations.requiredState);

export const VALIDATION_ZIP = Yup.string()
  .matches(NUMBERS_MATCHES, l10nEnService.validations.digitsOnly)
  .min(5, l10nEnService.validations.lengthEqual(5))
  .max(5, l10nEnService.validations.lengthEqual(5))
  .required(l10nEnService.validations.requiredZip);

export const VALIDATION_FIRST_NAME = Yup.string()
  .transform(v => v?.trim() || null)
  .matches(NAME_MATCHES, l10nEnService.validations.nameMatches)
  .max(64, l10nEnService.validations.lengthMatches(1, 64))
  .nullable()
  .required(l10nEnService.validations.enterFirstName);

export const VALIDATION_MIDDLE_NAME = Yup.string()
  .transform(v => v?.trim() ?? null)
  .matches(NAME_MATCHES, l10nEnService.validations.nameMatches)
  .max(64, l10nEnService.validations.lengthMatches(1, 64))
  .nullable()
  .notRequired();

export const VALIDATION_LAST_NAME = Yup.string()
  .transform(v => v?.trim() ?? null)
  .matches(NAME_MATCHES, l10nEnService.validations.nameMatches)
  .max(64, l10nEnService.validations.lengthMatches(1, 64))
  .nullable()
  .required(l10nEnService.validations.enterLastName);

export const VALIDATION_INSURANCE_PROVIDER_NAME = Yup.string()
  .matches(ENGLISH_MATCHES, l10nEnService.validations.couldContainEnglish)
  .max(64, l10nEnService.validations.lengthMatches(1, 64))
  .required(l10nEnService.validations.insuranceProviderName);

export const VALIDATION_INSURANCE_GROUP_NUMBER = Yup.string()
  .matches(NUMBERS_LETTERS_SPACE_HYPHEN_MATCHES, { message: l10nEnService.validations.couldContainEnglishWithSpace, excludeEmptyString: true })
  .max(20, l10nEnService.validations.lengthMatches(1, 20));

export const VALIDATION_INSURANCE_SUBSCRIBER_ID = Yup.string()
  .matches(NUMBERS_LETTERS_SPACE_HYPHEN_MATCHES, l10nEnService.validations.couldContainEnglishWithSpace)
  .max(20, l10nEnService.validations.lengthMatches(1, 20))
  .required(l10nEnService.validations.insuranceMemberId);

export const VALIDATION_LICENSE_NUMBER = Yup.string()
  .matches(LICENSE_MATCHES, l10nEnService.validations.licenseNumber)
  .max(20, l10nEnService.validations.lengthMatches(1, 20))
  .required(l10nEnService.validations.requiredLicenseNumber);

export const VALIDATION_PREFERRED_NAME = Yup.string()
  .nullable()
  .matches(ENGLISH_MATCHES, l10nEnService.validations.couldContainEnglish)
  .max(64, l10nEnService.validations.lengthMatches(1, 64));

export const extractNumbers = (value?: string) => value?.split(/\D/).join('') || null;

export const VALIDATION_HOME_PHONE = Yup.string()
  .transform(extractNumbers)
  .nullable()
  .matches(PHONE_MATCHES, l10nEnService.validations.phoneMatches)
  .min(10, l10nEnService.validations.lengthEqual(10))
  .max(10, l10nEnService.validations.lengthEqual(10));

export const VALIDATION_MOBILE_PHONE = Yup.string()
  .nullable()
  .transform(extractNumbers)
  .matches(PHONE_MATCHES, l10nEnService.validations.phoneMatches)
  .min(10, l10nEnService.validations.lengthEqual(10))
  .max(10, l10nEnService.validations.lengthEqual(10))
  .required(l10nEnService.validations.requiredMobilePhone);

export const VALIDATION_WORK_PHONE = Yup.string()
  .nullable()
  .transform(extractNumbers)
  .matches(PHONE_MATCHES, l10nEnService.validations.phoneMatches)
  .min(10, l10nEnService.validations.lengthEqual(10))
  .max(10, l10nEnService.validations.lengthEqual(10))
  .required(l10nEnService.validations.requiredWorkNumber);

export const VALIDATION_EMERGENCY_CONTACT_NAME = Yup.string()
  .matches(ENGLISH_MATCHES, l10nEnService.validations.couldContainEnglish)
  .max(64, l10nEnService.validations.lengthMatches(1, 64))
  .required(l10nEnService.validations.emergencyName);

export const VALIDATION_EMERGENCY_CONTACT_PHONE = Yup.string()
  .nullable()
  .transform(extractNumbers)
  .matches(PHONE_MATCHES, l10nEnService.validations.emergencyPhone)
  .min(10, l10nEnService.validations.lengthEqual(10))
  .max(10, l10nEnService.validations.lengthEqual(10))
  .required(l10nEnService.validations.requiredEmergencyPhone);

export const VALIDATION_EMERGENCY_CONTACT_RELATIONSHIP = Yup.string()
  .max(64, l10nEnService.validations.lengthMatches(1, 64));

const RFC2822Regex = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

export const isValidEmail = (email: string | undefined) => email ? RFC2822Regex.test(email) : true;
